/* eslint-disable @typescript-eslint/no-explicit-any */
import { computed, Injectable, NgZone, signal } from '@angular/core';
import { MessageService } from '../message/message.service';
import { LoggerService } from '../logger/logger.service';
import { ClientService } from '../client/client.service';
import { UserService } from '../user/user.service';
import { catchError, firstValueFrom, from, map, Observable, of, switchMap } from 'rxjs';
import { LogsType } from '../../../shared/constants/logs-type.enum';
import { MessageType } from '../../../shared/constants/message-type.enum';
import { CreateClientDto } from '../../models/client/create-client.dto';
import {
  Auth,
  signInWithEmailAndPassword,
  signInWithPopup,
  GoogleAuthProvider,
  createUserWithEmailAndPassword,
  sendPasswordResetEmail,
  updatePassword,
  reauthenticateWithCredential,
  EmailAuthProvider,
  signOut,
  User
} from '@angular/fire/auth';
import { Router } from '@angular/router';
import { ClientErrorType } from '../../../shared/constants/error-status.enum';
import { browserSessionPersistence, onAuthStateChanged, setPersistence } from 'firebase/auth';
import { FirebaseUserCreateDto } from '../../models/firebase/create-firebase-user.dto';

@Injectable({
  providedIn: 'root',
})
export class AuthenticationService {
  private readonly className = 'AuthenticationService';
  private functionName = '';
  private readonly _tokenSignal = signal<string | null>(null);
  private readonly _currentUserSignal = signal<User | null>(null);

  readonly tokenSignal = computed(() => this._tokenSignal());
  readonly currentUserSignal = computed(() => this._currentUserSignal());

  // Resolves once the first onAuthStateChanged callback completes,
  // so guards never run before role/token signals are populated.
  readonly authReady: Promise<void>;
  private _authReadyResolve!: () => void;

  constructor(
    private readonly ngZone: NgZone,
    private readonly firebaseAuth: Auth,
    private readonly logger: LoggerService,
    private readonly router: Router,
    private readonly clientService: ClientService,
    private readonly messageService: MessageService,
    private readonly userService: UserService) {
    this.authReady = new Promise<void>(resolve => {
      this._authReadyResolve = resolve;
    });
    // Session persistence: each tab keeps its own independent auth session.
    // onAuthStateChanged from another tab will NOT bleed into this tab.
    setPersistence(this.firebaseAuth, browserSessionPersistence)
      .then(() => this.initAuthListener())
      .catch(() => this.initAuthListener());
  }

  private initAuthListener(): void {
    onAuthStateChanged(this.firebaseAuth, async user => {
      this._currentUserSignal.set(user);

      if (user?.email) {
        await this.getToken();
        await this.userService.loadCurrentUserContext(user.email);
      } else {
        this._tokenSignal.set(null);
        this.userService.resetUserContext();
      }

      // Signal guards that auth state is fully initialised (no-op on subsequent calls)
      this._authReadyResolve();
    });
  }

  async getToken(): Promise<string | null> {
    this.functionName = 'getToken';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);

    try {
      const user = await this.firebaseAuth.currentUser;

      if (!user) {
        return null;
      }

      const token = await user.getIdToken(true);
      this._tokenSignal.set(token);

      return token;
    } catch (error) {
      this.logger.logMessage(LogsType.ERROR, this.className, this.functionName, 'Resultado:', error);
      return null;
    }
  }

  getEmailUser(): string {
    return this._currentUserSignal()?.email ?? '';
  }

  async emailSignIn(email: string, password: string): Promise<boolean> {
    this.functionName = 'emailSignIn';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);

    try {
      const credential = await signInWithEmailAndPassword(this.firebaseAuth, email, password);

      if (!credential.user) {
        this.logger.logMessage(LogsType.ERROR, this.className, this.functionName, 'Resultado: No user found');
        this.messageService.showMessages('logIn', MessageType.ERROR);
        return false;
      }

      await this.getToken();

      this.logger.logMessage(LogsType.LOG, this.className, this.functionName, 'Resultado:', credential.user);
      return true;
    } catch (error) {
      this.logger.logMessage(LogsType.ERROR, this.className, this.functionName, 'Resultado:', error);
      this.messageService.showMessages('logIn', MessageType.ERROR);
      return false;
    }
  }

  async googleSignIn(): Promise<boolean> {
    this.functionName = 'googleSignIn';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);

    try {
      const provider = new GoogleAuthProvider();
      const credential = await this.ngZone.run(() =>
        signInWithPopup(this.firebaseAuth, provider)
      );

      const user = credential.user;

      if (!user || !user.email) {
        this.messageService.showMessages('googleSignInFailed', MessageType.ERROR);
        return false;
      }

      await this.getToken();

      const usersResponse = await firstValueFrom(
        this.userService.getUsersByEmail(user.email)
      );

      if (usersResponse?.users?.length > 0) {
        this.logger.logMessage(LogsType.LOG, this.className, this.functionName, 'User founded:', user.email);

        return true;
      }

      const createClient: CreateClientDto = {
        name: user.displayName ?? '',
        email: user.email ?? ''
      };

      await firstValueFrom(
        this.clientService.createClient(createClient)
      );

      this.logger.logMessage(LogsType.LOG, this.className, this.functionName, 'User created:', user.email);

      return true;
    }
    catch (error)
    {
      this.logger.logMessage(LogsType.ERROR, this.className, this.functionName, 'Google sign-in failed:', error);
      this.messageService.showMessages('googleSignInFailed', MessageType.ERROR);

      return false;
    }
  }

  registerClient(firebaseUser: FirebaseUserCreateDto, clientDto: CreateClientDto): Observable<{ success: boolean; error?: string }> {
    if (!firebaseUser.password) {
      return of({ success: false, error: 'auth/invalid-password' });
    }

    return from(createUserWithEmailAndPassword(this.firebaseAuth, firebaseUser.email, firebaseUser.password)).pipe(
      switchMap(userCredential => {
        if (!userCredential.user) {
          return of({ success: false, error: 'auth/unexpected-error' });
        }

        return from(this.getToken());
      }),
      switchMap(() =>
        this.clientService.createClient(clientDto).pipe(
          map(() => ({ success: true }))
        )
      ),
      catchError(error => {
        switch (error?.code ?? error?.type ?? error?.status) {
          case 'auth/email-already-in-use':
            return of({ success: false, error: 'auth/email-already-in-use' });
          case 401:
            return of({ success: false, error: 'auth/invalid-permissions' });
          case 404:
            return of({ success: false, error: 'auth/not-found' });
          case 409:
            return of({ success: false, error: 'auth/conflict' });
          case ClientErrorType.CLIENT_NOT_CREATED:
          default:
            return of({ success: false, error: 'auth/unexpected-error' });
        }
      })
    );
  }

  resetPassword(email: string): Promise<void> {
    this.functionName = 'resetPassword';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);

    return sendPasswordResetEmail(this.firebaseAuth, email);
  }

  async logout(): Promise<boolean> {
    this.functionName = 'logout';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);

    return signOut(this.firebaseAuth).then(() => {
      this.logger.logMessage(LogsType.LOG, this.className, this.functionName, 'Logout successful');
      this._tokenSignal.set(null);
      this.router.navigate(['/account/login']);
      return true;
    }).catch(error => {
      this.logger.logMessage(LogsType.ERROR, this.className, this.functionName, 'Logout failed:', error);
      this.messageService.showMessages('logOut', MessageType.ERROR);
      return false;
    });
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    this.functionName = 'changePassword';
    this.logger.logMessage(LogsType.TRACE, this.className, this.functionName);

    const user = this.firebaseAuth.currentUser;

    if (!user || !user.email) {
      this.messageService.showMessages('unexpectedError', MessageType.ERROR);
      return;
    }

    if (!currentPassword) {
      this.messageService.showMessages('currentPasswordRequired', MessageType.WARNING);
      return;
    }

    try {
      const credentials = EmailAuthProvider.credential(
        user.email,
        currentPassword
      );

      await reauthenticateWithCredential(user, credentials);
      await updatePassword(user, newPassword);

      this.messageService.showMessages('passwordUpdated', MessageType.SUCCESS);
    }
    catch (error: any) {
      this.logger.logMessage(LogsType.ERROR, this.className, this.functionName, 'Password update failed:', error);

      if (error?.code === 'auth/wrong-password') {
        this.messageService.showMessages('reauthenticationFailed', MessageType.ERROR);
      } else if (error?.code === 'auth/weak-password') {
        this.messageService.showMessages('weakPassword', MessageType.WARNING);
      } else if (error?.code === 'auth/invalid-credential') {
        this.messageService.showMessages('invalidCredential', MessageType.ERROR);
      }
      else {
        this.messageService.showMessages('passwordUpdate', MessageType.ERROR);
      }
    }
  }
}
